import { createFileRoute, useNavigate } from "@tanstack/react-router";
import ReactDOMServer from "react-dom/server";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
// @ts-expect-error three.js examples lack type declarations
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
// @ts-expect-error three.js examples lack type declarations
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// @ts-expect-error three.js examples lack type declarations
import { Line2 } from "three/examples/jsm/lines/Line2";
import {
  fetchMetrics,
  fetchAiSummary,
} from "@/vmeAiApiClient/adapter";
// @ts-expect-error three.js examples lack type declarations
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
// @ts-expect-error three.js examples lack type declarations
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { useQuery } from "@tanstack/react-query";
import { FaInfoCircle } from "react-icons/fa";

const ROVTooltip = ({ data }: { data: any }) => (
  <div className="min-w-[200px] rounded-lg bg-white/90 p-3 shadow-lg">
    <h4 className="text-lg font-bold text-gray-800">ROV Data Point</h4>
    <hr className="my-2 border-gray-300" />
    <div className="space-y-1">
      <p className="text-sm text-gray-700">Coral Count: {data.coral_count}</p>
      <p className="text-sm text-gray-700">Sponge Count: {data.sponge_count}</p>
      <p className="text-sm text-gray-700">Depth: {data.depth.toFixed(1)}m</p>
      <p className="text-sm text-gray-700">
        Time: {new Date(data.time_value).toLocaleTimeString()}
      </p>
      <p className="text-sm text-gray-700">Cruise: {data.cruise_id}</p>
      <p className="text-sm text-gray-700">
        Salinity: {data.salinity.toFixed(2)} PSU
      </p>
      <p className="text-sm text-gray-700">
        Oxygen: {data.oxygen.toFixed(2)} ml/L
      </p>
      <p className="text-sm text-gray-700">
        Temperature: {data.temperature.toFixed(2)}°C
      </p>
    </div>
  </div>
);

export const Route = createFileRoute("/mockRovAssets/$lowering/digitalTwin/")({
  component: DigitalTwinPage,
  loader: async ({ context: { queryClient }, params: { lowering } }) => {
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: ["rovMetrics", lowering],
        queryFn: () => fetchMetrics(lowering),
      }),
      queryClient.prefetchQuery({
        queryKey: ["rovData", lowering],
        queryFn: () => fetchAiSummary(lowering),
      }),
    ]);
  },
});

function DigitalTwinPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<any>(null);
  const rovLineRef = useRef<any>(null);
  const [showRuler, setShowRuler] = useState(false);
  const [tooltip, setTooltip] = useState({ show: false, text: "", html: "", x: 0, y: 0 });
  const [phylum, setPhylum] = useState("coral");
  const [hidePhylum, setHidePhylum] = useState(false);
  const [selectedEnvironmentalParam, setSelectedEnvironmentalParam] =
    useState("temperature");
  const [environmentalParamUnit, setEnvironmentalParamUnit] = useState("°C");
  const [cameraState, setCameraState] = useState<any>(null);

  const navigate = useNavigate();
  const { lowering } = Route.useParams();
  const { data: metrics } = useQuery({
    queryKey: ["rovMetrics", lowering],
    queryFn: () => fetchMetrics(lowering),
    enabled: !!lowering,
  });
  const { data: ai_summary } = useQuery({
    queryKey: ["rovData", lowering],
    queryFn: () => fetchAiSummary(lowering),
    enabled: !!lowering,
  });

  useEffect(() => {
    if (!mountRef.current || !metrics || !ai_summary) return;

    const mount = mountRef.current;
    const scene = createScene();
    const camera = createCamera();
    const renderer = createRenderer(mount);
    const controls = createControls(camera, renderer);

    // Restore camera state if it exists
    if (cameraState) {
      camera.position.copy(cameraState.position);
      controls.target.copy(cameraState.target);
      controls.update();
    }

    const raycaster = new THREE.Raycaster();
    raycaster.params.Line2 = { threshold: 5 };
    const mouse = new THREE.Vector2();

    const onMouseMove = (event: any) => {
      setCameraState({
        position: camera.position.clone(),
        target: controls.target.clone(),
      });

      const rect = mount.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);

      if (rovLineRef.current) {
        const intersects = raycaster.intersectObjects(
          rovLineRef.current.children,
          true
        );

        // Reset all spheres first
        rovLineRef.current.children.forEach((sphere: any) => {
          sphere.material.opacity = 0.8; // Reset to default opacity
          sphere.material.emissive = new THREE.Color(0x000000);
          sphere.material.emissiveIntensity = 0;
        });

        if (intersects.length > 0) {
          const intersectedSphere = intersects[0].object as any;
          const dataPoint = intersectedSphere.userData.dataPoint;

          // Set hover effects for intersected sphere
          intersectedSphere.material.opacity = 1; // Full opacity on hover
          intersectedSphere.material.emissive = new THREE.Color(
            phylum === "coral" ? 0xff0000 : 0x800080
          );
          intersectedSphere.material.emissiveIntensity = 0.5;

          setTooltip({
            show: true,
            text: "",
            html: ReactDOMServer.renderToString(
              <ROVTooltip data={dataPoint} />
            ),
            x: event.clientX,
            y: event.clientY,
          });
        } else {
          setTooltip((prev) => ({ ...prev, show: false }));
        }
      }
    };

    window.addEventListener("mousemove", onMouseMove);

    const sceneGroup = new THREE.Group();
    scene.add(sceneGroup);

    sceneGroup.rotation.x = -Math.PI / 2; // -90 degrees around X axis

    addLighting(sceneGroup);

    addROVPlot(
      sceneGroup,
      rovLineRef,
      phylum,
      metrics,
      ai_summary,
      hidePhylum,
      selectedEnvironmentalParam
    );
    loadBathymetry(sceneGroup, metrics);

    loadRulerObject(sceneGroup).then((ruler: any) => {
      rulerRef.current = ruler;
      ruler.visible = showRuler;
    });

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", handleResize);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [
    phylum,
    mountRef,
    metrics,
    ai_summary,
    hidePhylum,
    selectedEnvironmentalParam,
  ]);

  const handleRulerToggle = () => {
    if (rulerRef.current) {
      rulerRef.current.visible = !showRuler;
      setShowRuler(!showRuler);
    }
  };

  return (
    <div>
      {metrics ? (
        <>
          <div ref={mountRef} />
          {tooltip.show && (
            <div
              className="pointer-events-none fixed z-50"
              style={{
                left: `${tooltip.x + 10}px`,
                top: `${tooltip.y + 10}px`,
              }}
              dangerouslySetInnerHTML={{ __html: tooltip.html }}
            />
          )}
          <div className="absolute bottom-4 right-4 rounded-lg bg-white p-4 shadow-lg">
            <div className="mb-4 border-b pb-2 text-sm text-gray-600">
              <p className="mb-1 font-bold">Controls:</p>
              <ul className="list-inside list-disc space-y-1">
                <li>Left click + drag to rotate</li>
                <li>Scroll or pinch to zoom</li>
                <li>Right click + drag to pan</li>
                <li>Shift + left click + drag to translate</li>
              </ul>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleRulerToggle}
                className="rounded bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                <div className="flex items-center">
                  {showRuler ? "Hide Scale" : "Show 1km Scale"}
                  <div className="group relative ml-1">
                    <FaInfoCircle className="cursor-help text-gray-600" />
                    <div className="pointer-events-none absolute bottom-full left-1/2 mb-4 hidden w-48 -translate-x-1/2 rounded bg-black p-2 text-center text-xs text-white group-hover:block">
                      A static 1km-long ruler, to be used as a reference scale
                      to help understand the size of features in the
                      visualization
                    </div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setHidePhylum(!hidePhylum)}
                className="ml-2 rounded bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                {hidePhylum ? "Show Phylum" : "Hide Phylum"}
              </button>
            </div>
            <div className="mt-4 flex space-x-2">
              <button
                onClick={() => setPhylum("coral")}
                className={`rounded px-4 py-2 ${
                  phylum === "coral"
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 text-black"
                } transition-colors hover:bg-red-600`}
              >
                Coral
              </button>
              <button
                onClick={() => setPhylum("sponge")}
                className={`rounded px-4 py-2 ${
                  phylum === "sponge"
                    ? "bg-purple-500 text-white"
                    : "bg-gray-200 text-black"
                } transition-colors hover:bg-purple-600`}
              >
                Sponge
              </button>
            </div>
            <div className="mt-4 flex space-x-2">
              <button
                onClick={() => {
                  setSelectedEnvironmentalParam("temperature");
                  setEnvironmentalParamUnit("°C");
                }}
                className={`rounded px-4 py-2 ${
                  selectedEnvironmentalParam === "temperature"
                    ? "bg-gray-500 text-white"
                    : "bg-gray-200 text-black"
                } transition-colors hover:bg-gray-600 hover:text-white`}
              >
                Temperature
              </button>
              <button
                onClick={() => {
                  setSelectedEnvironmentalParam("salinity");
                  setEnvironmentalParamUnit("PSU");
                }}
                className={`rounded px-4 py-2 ${
                  selectedEnvironmentalParam === "salinity"
                    ? "bg-gray-500 text-white"
                    : "bg-gray-200 text-black"
                } transition-colors hover:bg-gray-600 hover:text-white`}
              >
                Salinity
              </button>
              <button
                onClick={() => {
                  setSelectedEnvironmentalParam("oxygen");
                  setEnvironmentalParamUnit("ml/L");
                }}
                className={`rounded px-4 py-2 ${
                  selectedEnvironmentalParam === "oxygen"
                    ? "bg-gray-500 text-white"
                    : "bg-gray-200 text-black"
                } transition-colors hover:bg-gray-600 hover:text-white`}
              >
                Oxygen
              </button>
            </div>
            <div className="mt-4 flex justify-center space-x-8">
              <div className="flex flex-col items-center">
                <div className="text-xs font-bold">
                  {metrics.min_bathy_depth}
                </div>
                <div className="h-32 w-4 border border-black bg-gradient-to-t from-violet-500 via-yellow-500 to-red-500"></div>
                <div className="text-xs font-bold">
                  {metrics.max_bathy_depth}
                </div>
                <p className="mt-1 text-center text-xs">Depth (m)</p>
              </div>
              {metrics && ai_summary && (
                <div className="flex flex-col items-center">
                  <div className="text-xs font-bold">
                    {Math.min(
                      ...ai_summary.map(
                        (item: any) => item[selectedEnvironmentalParam]
                      )
                    ).toFixed(2)}
                  </div>
                  <div className="h-32 w-4 border border-black bg-gradient-to-t from-[#FF0000] to-[#0000FF]"></div>
                  <div className="text-xs font-bold">
                    {Math.max(
                      ...ai_summary.map(
                        (item: any) => item[selectedEnvironmentalParam]
                      )
                    ).toFixed(2)}
                  </div>
                  <p className="mt-1 text-center text-xs">
                    {selectedEnvironmentalParam} ({environmentalParamUnit})
                  </p>
                </div>
              )}
            </div>
            <div className="mt-4">
              <button
                onClick={() => navigate({ to: `/mockRovAssets/${lowering}` })}
                className="w-full rounded bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                Back to Summary
              </button>
            </div>
          </div>
        </>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  );
}

const createScene = () => {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);
  return scene;
};

const createCamera = () => {
  const camera = new THREE.PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    1,
    20000
  );
  camera.position.set(1000, 300, 3000);
  return camera;
};

const createRenderer = (mount: any) => {
  const renderer = new THREE.WebGLRenderer();
  renderer.setSize(window.innerWidth, window.innerHeight);
  mount.appendChild(renderer.domElement);
  return renderer;
};

const createControls = (camera: any, renderer: any) => {
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.minDistance = 0.1;
  controls.maxDistance = 5000;

  controls.target.set(0, 0, -500);
  controls.update();

  return controls;
};

const addLighting = (group: any) => {
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
  group.add(ambientLight);
  const sunLight = new THREE.DirectionalLight(0xffffff, 1.5);
  sunLight.position.set(1, 1.8, 2);
  group.add(sunLight);
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.2);
  fillLight.position.set(-1, 0.5, -1);
  group.add(fillLight);
};

const addROVPlot = (
  group: any,
  rovLineRef: any,
  phylum: any,
  metrics: any,
  data: any,
  hidePhylum: any,
  environmentalParam: any
) => {
  const positions: any[] = [];
  const colors: any[] = [];
  const dataPoints = [];

  const parameterValues = data.map((item: any) => item[environmentalParam]);
  const minParamValue = Math.min(...parameterValues);
  const maxParamValue = Math.max(...parameterValues);

  data.forEach((item: any) => {
    const x = item.utm_x - metrics["Mean UTM X"];
    const y = item.utm_y - metrics["Mean UTM Y"];
    const z = -item.depth;

    positions.push(x, y, z);
    dataPoints.push(item);

    // Calculate normalized parameter value (0 to 1)
    const normalizedParameter =
      (item[environmentalParam] - minParamValue) /
      (maxParamValue - minParamValue);

    // Create a color that matches the legend exactly (blue to red)
    const color = new THREE.Color();
    color
      .setStyle(normalizedParameter === 1 ? "#FF0000" : "#0000FF")
      .lerp(
        new THREE.Color(normalizedParameter === 0 ? "#0000FF" : "#FF0000"),
        normalizedParameter
      );

    colors.push(color.r, color.g, color.b);
  });

  // Create the line
  const geometry = new LineGeometry();
  geometry.setPositions(positions);
  geometry.setColors(colors);

  const material = new LineMaterial({
    vertexColors: true,
    linewidth: 6,
    transparent: true,
    opacity: 0.5,
    dashed: false,
  });

  const line = new Line2(geometry, material);
  line.computeLineDistances();
  line.scale.set(1, 1, 1);
  group.add(line);
  addEnvironmentalLegend(minParamValue, maxParamValue);

  if (!hidePhylum) {
    const sphereGroup = new THREE.Group();
    const maxCount = Math.max(...data.map((d: any) => d[`${phylum}_count`]));
    const minRadius = 0;
    const maxRadius = 10;

    data.forEach((item: any) => {
      const x = item.utm_x - metrics["Mean UTM X"];
      const y = item.utm_y - metrics["Mean UTM Y"];
      const z = -item.depth;

      const radius =
        minRadius +
        (item[`${phylum}_count`] / maxCount) * (maxRadius - minRadius);
      const sphereGeometry = new THREE.SphereGeometry(radius, 16, 16);
      const sphereMaterial = new THREE.MeshPhongMaterial({
        color: phylum === "coral" ? 0xff0000 : 0x800080, // Red for coral, purple for sponge
        transparent: true,
        opacity: 0.8,
        emissive: 0x000000,
        emissiveIntensity: 0,
      });
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);

      sphere.position.set(x, y, z);
      sphere.userData.dataPoint = item;
      sphereGroup.add(sphere);
    });

    group.add(sphereGroup);
    rovLineRef.current = sphereGroup;
  }
};

const addEnvironmentalLegend = (minParamValue: any, maxParamValue: any) => {
  // Add this to your JSX return statement, inside the controls div
  return (
    <div className="mt-4">
      <div className="flex flex-col items-center">
        <div className="text-xs font-bold">{maxParamValue.toFixed(2)}</div>
        <div className="h-32 w-4 border border-black bg-gradient-to-t from-blue-500 to-red-500"></div>
        <div className="text-xs font-bold">{minParamValue.toFixed(2)}</div>
      </div>
      <p className="mt-3 text-center text-xs">Salinity (PSU)</p>
    </div>
  );
};

const loadBathymetry = (group: any, metrics: any) => {
  const loader = new OBJLoader();
  loader.load(
    `/mockROVData/reports/${metrics.Lowering}/bathymetry_${metrics.Lowering}.obj`,
    (object: any) => {
      object.traverse((child: any) => {
        if (child.isMesh) {
          processMesh(child, metrics);
        }
      });
      group.add(object);
      object.position.set(0, 0, -50);
    }
  );
};

const processMesh = (mesh: any, metrics: any) => {
  const geometry = mesh.geometry;
  geometry.computeVertexNormals();

  const positions = geometry.attributes.position;
  const colors = createColorArray(positions, metrics);

  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  mesh.material = createMeshMaterial();
};

const createColorArray = (positions: any, metrics: any) => {
  const colors = new Float32Array(positions.count * 3);

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i) - metrics["Mean UTM X"];
    const y = positions.getY(i) - metrics["Mean UTM Y"];
    positions.setXY(i, x, y);

    const depth = -positions.getZ(i);
    const normalizedZ =
      (depth - metrics.min_bathy_depth) /
      (metrics.max_bathy_depth - metrics.min_bathy_depth);

    // Create a color that matches the legend (violet -> yellow -> red)
    const color = new THREE.Color();
    if (normalizedZ <= 0.5) {
      // Interpolate from violet to yellow in the first half
      color
        .setStyle("#FF0000")
        .lerp(new THREE.Color("#FFFF00"), normalizedZ * 2);
    } else {
      // Interpolate from yellow to red in the second half
      color
        .setStyle("#FFFF00")
        .lerp(new THREE.Color("#8B00FF"), (normalizedZ - 0.5) * 2);
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  return colors;
};

const createMeshMaterial = () => {
  return new THREE.MeshPhongMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    shininess: 50,
    flatShading: false,
  });
};

const loadRulerObject = (group: any) => {
  const loader = new OBJLoader();
  return new Promise((resolve) => {
    loader.load("/mockROVData/ruler.obj", (object: any) => {
      group.add(object);
      object.position.set(0, -500, 0);
      object.scale.set(10, 10, 2);
      object.rotateY(Math.PI / 2);
      object.visible = false;
      resolve(object);
    });
  });
};
